import { StatusInterface, UserUpdatePasswordRequestInterface } from '@elearning-platform/api-types/interfaces';
import { AxiosError, AxiosResponse } from 'axios';

import {
  FetchRegistrationFormParams,
  RefreshAccessTokenResponse,
  RegistrationForm,
  RemindPasswordInfo,
  SendActivationFormParams,
  SendRegistrationFormParams,
  SendRegistrationFormResponse,
  SendRegistrationFormWithTokenParams,
  UserDocument,
} from '../../interfaces';
import { ApiResolve } from '../../types/api';
import { api } from '../api';
import { phpApi } from '../default';

export const fetchRegistrationForm: (
  params: FetchRegistrationFormParams
) => Promise<RegistrationForm> = (
  params: FetchRegistrationFormParams
): Promise<RegistrationForm> => new Promise(
  (resolve: ApiResolve<RegistrationForm>, reject: (reason: AxiosError<{ message?: string }>) => void): Promise<void> => (
    phpApi.get(`/api/user/register${params.hash ? `/${params.hash}` : ''}`)
      .then((response: AxiosResponse<RegistrationForm>): void => resolve(response.data))
      .catch((err: AxiosError<{ message?: string }>): void => {
        const error: AxiosError<{ message?: string }> = err as AxiosError<{ message?: string }>;

        reject({ message: error.response?.data?.message } as AxiosError<{ message?: string }>);
      })
  )
);

export const sendRegistrationForm: (
  params: SendRegistrationFormParams
) => Promise<SendRegistrationFormResponse> = (
  params: SendRegistrationFormParams
): Promise<SendRegistrationFormResponse> => new Promise(
  (
    resolve: ApiResolve<SendRegistrationFormResponse>,
    reject: (reason: AxiosError<RegistrationForm>) => void
  ): Promise<void | never[]> => (
    phpApi.post('/api/user/register', params.data)
      .then((response: AxiosResponse<SendRegistrationFormResponse>): void => resolve(response.data))
      .catch((err: AxiosError<RegistrationForm>): void => {
        reject(err);
      })
  )
);

export const sendRegistrationFormWithToken: (
  params: SendRegistrationFormWithTokenParams
) => Promise<SendRegistrationFormResponse> = (
  params: SendRegistrationFormWithTokenParams
): Promise<SendRegistrationFormResponse> => new Promise(
  (
    resolve: ApiResolve<SendRegistrationFormResponse>,
    reject: (reason: AxiosError<RegistrationForm>) => void
  ): Promise<void | never[]> => (
    phpApi.post(`/api/user/register/${params.hash}`, params.data)
      .then((response: AxiosResponse<SendRegistrationFormResponse>): void => resolve(response.data))
      .catch((err: AxiosError<RegistrationForm>): void => {
        reject(err);
      })
  )
);

export const getUserDocuments: () => Promise<AxiosResponse<UserDocument[]>>
  = (): Promise<AxiosResponse<UserDocument[]>> => phpApi.get('/api/user/documents');

export const remindPassword: (login: string) => Promise<AxiosResponse<RemindPasswordInfo>> = (
  login: string
): Promise<AxiosResponse<RemindPasswordInfo>> => phpApi.post('/api/user/remind-password', { login });

export const fetchActivationForm: (
  token: string | null
) => Promise<RegistrationForm> = (
  token: string | null
): Promise<RegistrationForm> => new Promise(
  (resolve: ApiResolve<RegistrationForm>, reject: (reason: AxiosError<{ message?: string }>) => void): Promise<void> => (
    phpApi.get(`/api/user/activation/${token}`)
      .then((response: AxiosResponse<RegistrationForm>): void => resolve(response.data))
      .catch((err: AxiosError<{ message?: string }>): void => {
        const error: AxiosError<{ message?: string }> = err as AxiosError<{ message?: string }>;

        reject({ message: error.response?.data?.message } as AxiosError<{ message?: string }>);
      })
  )
);

export const sendActivationForm: (
  params: SendActivationFormParams
) => Promise<SendRegistrationFormResponse> = (
  params: SendActivationFormParams
): Promise<SendRegistrationFormResponse> => new Promise(
  (
    resolve: ApiResolve<SendRegistrationFormResponse>,
    reject: (reason: AxiosError<RegistrationForm>) => void
  ): Promise<void | never[]> => (
    phpApi.post(`/api/user/activation/${params.token}`, params.data)
      .then((response: AxiosResponse<SendRegistrationFormResponse>): void => resolve(response.data))
      .catch((err: AxiosError<RegistrationForm>): void => {
        reject(err);
      })
  )
);

export const updatePassword: (payload: UserUpdatePasswordRequestInterface) => Promise<StatusInterface>
= (payload: UserUpdatePasswordRequestInterface): Promise<StatusInterface> => api.post('v1/auth/update-password', payload);

export const refreshAccessToken: () => Promise<RefreshAccessTokenResponse>
  = (): Promise<RefreshAccessTokenResponse> => phpApi.post('/api/user/refresh', undefined, { withCredentials: true });

export const extendRefreshToken: () => Promise<void>
  = (): Promise<void> => phpApi.post('/api/user/extend-session', undefined, { withCredentials: true });

export const approveUserDeactivation: (userId: number) => Promise<void> = (userId: number): Promise<void> => (
  phpApi.post(`/api/user/${userId}/deactivation-request/approve`)
);

export const rejectUserDeactivation: (userId: number) => Promise<void> = (userId: number): Promise<void> => (
  phpApi.post(`/api/user/${userId}/deactivation-request/reject`)
);
